import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: 'Insights & Articles | Hei Web Creations Singapore',
    description:
      'Tech-insider articles on AI agents, native apps, and web performance from Hei Web Creations — a premium web design studio in Singapore.',
  });

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then((res) => setPosts(res.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="pt-32 bg-white min-h-screen" data-testid="blog-page">
      {/* Header */}
      <section className="py-20 border-b border-gray-100">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <span className="font-mono text-sm text-[#C8102E] tracking-tight">
              ~/insights
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#1A1A1A] mt-4 mb-6 tracking-tight">
              The Field Notes
            </h1>
            <p className="text-xl text-gray-500 leading-relaxed max-w-2xl">
              Direct, no-fluff writing on AI, mobile, and web performance — for
              operators who care about ROI.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Posts */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          {loading ? (
            <p className="font-mono text-gray-400" data-testid="blog-loading">Loading posts…</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {posts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  viewport={{ once: true }}
                  data-testid={`blog-card-${post.slug}`}
                >
                  <Link to={`/blog/${post.slug}`} className="group block">
                    <div className="relative overflow-hidden rounded-2xl aspect-[16/10] mb-6 bg-[#FAFAFA]">
                      <motion.img
                        src={post.cover_image}
                        alt={`Cover image for '${post.title}' — ${post.category} article by Hei Web Creations`}
                        loading="lazy"
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.6 }}
                      />
                    </div>
                    <div className="flex items-center gap-3 mb-3 font-mono text-xs">
                      <span className="text-[#C8102E] uppercase tracking-wider">
                        {post.category}
                      </span>
                      <span className="text-gray-300">/</span>
                      <span className="text-gray-400">{post.read_time}</span>
                    </div>
                    <h2 className="text-xl font-bold text-[#1A1A1A] leading-snug group-hover:text-[#C8102E] transition-colors duration-300">
                      {post.title}
                    </h2>
                    <p className="text-gray-500 mt-3 leading-relaxed line-clamp-3">
                      {post.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-1 mt-4 font-mono text-sm text-[#1A1A1A] group-hover:gap-2 transition-all duration-300">
                      Read article <ArrowUpRight size={16} />
                    </span>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
